import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LINKS } from '@trip-ux/constants';
import { apiClient } from '../api.client';
import { RootStateOrAny } from 'react-redux';
import { Dispatch } from 'redux';
import { omitBy } from 'lodash';
import { ApiError } from './types';

const NEW = 'NEW';
const CA = 'CA';

export interface UserSlice {
  isRegisterSuccess: boolean;
  isRegisterFailure: boolean;
  registerError: ApiError | null;

  isFetchProfileLoading: boolean;
  isFetchProfileSuccess: boolean;
  isFetchProfileFailure: boolean;
  profile: any;
  orgHierarchy: any;

  isHierarchyLoading: boolean;
  isHierarchySuccess: boolean;
  isHierarchyFailure: boolean;

  isUpdateSuccess: boolean;
  isUpdateFailure: boolean;
  updateError: ApiError | null;
}

const initialState: UserSlice = {
  // User registration
  isRegisterSuccess: false,
  isRegisterFailure: false,
  registerError: null,

  // User update
  isUpdateSuccess: false,
  isUpdateFailure: false,
  updateError: null,

  // User fetching
  isFetchProfileLoading: false,
  isFetchProfileSuccess: false,
  isFetchProfileFailure: false,
  profile: null,

  // Org Hierarchy
  isHierarchyLoading: false,
  isHierarchySuccess: false,
  isHierarchyFailure: false,
  orgHierarchy: []
};

const formatLicenses = selectedProvinces => {
  return selectedProvinces.map((licensedProvince: string) => {
    return { licensedProvince };
  });
};

const filterDeletedLicenses = profile => {
  return profile?.licenses?.filter(license => license.isDeleted !== true);
};

const formatBranches = (selectedBranches, agencyBranch) => {
  return selectedBranches.map(branch => ({
    organizationCode: branch,
    default: branch === agencyBranch
  }));
};

const prepareRegisterPayload = payload => {
  const { referenceNumber, profile, selectedProvinces, selectedBranches, profileCode } = payload;

  return {
    referenceNumber,
    profileCode: profile?.userId,
    role: profile?.role,
    title: profile?.title,
    status: NEW,
    organizations: formatBranches(selectedBranches, profile?.agencyBranch),
    firstName: profile?.firstName,
    lastName: profile?.lastName,
    language: profile?.preferredLang,
    city: profile?.city,
    province: profile?.province,
    country: CA,
    email: profile?.emailAddress,
    createdBy: profileCode,
    licenses: formatLicenses(selectedProvinces)
  };
};

const prepareUpdatePayload = payload => {
  const { formData, licenses, organizations } = payload;
  return omitBy(
    {
      profileCode: formData?.userId,
      role: formData?.role,
      title: formData?.title,
      status: formData?.status,
      organizations,
      firstName: formData?.firstName,
      lastName: formData?.lastName,
      language: formData?.preferredLang,
      city: formData?.city,
      province: formData?.province,
      country: 'CA',
      email: formData?.emailAddress,
      licenses
    },
    value => value === null || value === undefined || value === ''
  );
};

export const registerUser = createAsyncThunk(
  'user/createProfile',
  async (
    payload: any,
    {
      getState,
      dispatch,
      rejectWithValue
    }: { getState: () => RootStateOrAny; dispatch: Dispatch; rejectWithValue: Function }
  ) => {
    try {
     const profileCode = getState().userProfile.profileCode;
      const profilePayload = prepareRegisterPayload({ ...payload, profileCode });
      return await apiClient(dispatch, getState).post(LINKS.CREATE_PROFILE, { ...profilePayload });
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async (
    payload: any,
    {
      getState,
      dispatch,
      rejectWithValue
    }: { getState: () => RootStateOrAny; dispatch: Dispatch; rejectWithValue: Function }
  ) => {
    try {
      const profileRef = getState().userProfile.userRef;
      const userProfileRef = getState().user.profile.profileRef;
      const profilePayload = prepareUpdatePayload({ ...payload, profileRef });
      const config = {
        params: { profileRef: userProfileRef }
      };
      return await apiClient(dispatch, getState).put(LINKS.UPDATE_PROFILE, { ...profilePayload }, config);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchOrgHierarchy = createAsyncThunk(
  'user/fetchOrgHierarchy',
  async (code: string, { getState, dispatch }: { getState: () => RootStateOrAny; dispatch: Dispatch }) => {
    return apiClient(dispatch, getState).get(LINKS.ORG_HIERARCHY, { params: { code } });
  }
);

export const fetchUser = createAsyncThunk(
  'user/fetchProfile',
  async (payload: any, { getState, dispatch }: { getState: () => RootStateOrAny; dispatch: Dispatch }) => {
    return apiClient(dispatch, getState).get(LINKS.PROFILE, { params: { profileRef: payload } });
  }
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(registerUser.fulfilled, state => {
        state.isRegisterSuccess = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isRegisterSuccess = false;
        state.isRegisterFailure = true;
        state.registerError = action.payload as ApiError;
      })
      .addCase(fetchUser.pending, state => {
        state.isFetchProfileLoading = true;
        state.isFetchProfileFailure = false;
        state.isFetchProfileSuccess = false;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isFetchProfileLoading = false;
        state.isFetchProfileFailure = false;
        state.isFetchProfileSuccess = true;
        state.profile = action.payload;
        state.profile.licenses = filterDeletedLicenses(action.payload);
      })
      .addCase(fetchUser.rejected, state => {
        state.isFetchProfileFailure = true;
        state.isFetchProfileLoading = false;
        state.isFetchProfileSuccess = false;
      })
      .addCase(fetchOrgHierarchy.pending, state => {
        state.isHierarchyLoading = true;
      })
      .addCase(fetchOrgHierarchy.fulfilled, (state, action) => {
        state.isHierarchyLoading = false;
        state.isHierarchySuccess = true;
        state.orgHierarchy = action.payload;
      })
      .addCase(fetchOrgHierarchy.rejected, state => {
        state.isHierarchyLoading = false;
        state.isHierarchyFailure = true;
      })
      .addCase(updateUser.fulfilled, state => {
        state.isUpdateSuccess = true;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isUpdateFailure = true;
        state.updateError = action.payload as ApiError;
      });
  }
});

export const { resetState } = userSlice.actions;

export const selectRegisterSuccess = state => state.user.isRegisterSuccess;
export const selectRegisterFailure = state => state.user.isRegisterFailure;

export const selectUpdateSuccess = state => state.user.isUpdateSuccess;
export const selectUpdateFailure = state => state.user.isUpdateFailure;

export const selectFetchProfileLoading = state => state.user.isFetchProfileLoading;
export const selectFetchProfileFailure = state => state.user.isFetchProfileFailure;
export const selectFetchProfileSuccess = state => state.user.isFetchProfileSuccess;
export const selectProfile = state => state.user.profile;

export const selectHierarchyLoading = state => state.user.isHierarchyLoading;
export const selectHierarchySuccess = state => state.user.isHierarchySuccess;
export const selectHierarchyFailure = state => state.user.isHierarchyFailure;
export const selectOrganizationHierarchy = state => state.user.orgHierarchy;

export const selectRegisterError = state => state.user.registerError;
export const selectUpdateError = state => state.user.updateError;

export default userSlice.reducer;
